import { FC, useState, useEffect, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router'
import { Breadcrumbs, Button, Input } from '@mondra/ui-components'
import { useQuery } from '@apollo/client'
import { IGeneric } from './types'
import { getGenericsVars } from './helpers'
import { filterEnums, getTableData } from '../../../shared/gridFunctions'
import Table from '../../../components/Table'
import { ExportGraphqlButton } from '../../../components/ExportGraphqlButton'
import { GET_GENERICS_ALL } from '../../../query/generics'
import { EXPORT_GENERICS_QUERY } from '../../../query/export-generics'
import { EXPORT_MAX_ROW_COUNT, ROUTES } from '../../../const'

const Generics: FC = () => {
  const navigate = useNavigate()

  const [tableData, setTableData] = useState<IGeneric[]>([])
  const [columnDefs, setColumn] = useState([])
  const [tableSearch, setTableSearch] = useState<string | null>(null)
  const [debouncedTableSearch, setDebouncedTableSearch] = useState(tableSearch)
  const [totalCount, setTotalCount] = useState<string>('')
  const [endCursor, setEndCursor] = useState<string | null>(null)
  const [startCursor, setStartCursor] = useState<string | null>(null)
  const [genericsVars, setGenericsVars] = useState<any>(getGenericsVars)
  const [order, setOrder] = useState([{ id: 'ASC' }])

  const { data, loading, refetch } = useQuery(GET_GENERICS_ALL, {
    variables: {
      str: '',
      first: 100,
      last: null,
      order: order,
      ...getGenericsVars,
    },
  })

  const anyRefetch = useCallback(
    (variables?: any): Promise<any> => {
      return refetch(variables)
    },
    [refetch]
  )

  const setCursors = useCallback(() => {
    setEndCursor(data.generics.pageInfo.endCursor)
    setStartCursor(data.generics.pageInfo.startCursor)
  }, [data])

  useEffect(() => {
    const timer = setTimeout(() => setTableSearch(debouncedTableSearch), 1000)
    return () => clearTimeout(timer)
  }, [debouncedTableSearch])

  useEffect(() => {
    refetch()
    getTableData(
      data,
      loading,
      setTableData,
      setColumn,
      setTotalCount,
      setCursors,
      ROUTES.generic,
      'generics',
      'generic'
    )
  }, [data, loading, refetch, setCursors])

  useEffect(() => {
    if (tableSearch != null || !order[0].id) {
      if (!loading) {
        setGenericsVars({ ...filterEnums(tableSearch, getGenericsVars, data) })
        anyRefetch({
          str: tableSearch || '',
          ...filterEnums(tableSearch, getGenericsVars, data),
          after: null,
          before: null,
          first: 100,
          last: null,
          order: order,
        })
      }
    }
  }, [tableSearch, order, loading, data, anyRefetch])

  const exportPayload = useMemo(
    () => ({
      operationName: 'Generics',
      query: EXPORT_GENERICS_QUERY.loc?.source.body,
      variables: {
        first: EXPORT_MAX_ROW_COUNT,
        after: null,
        where: null,
        order,
      },
    }),
    [order]
  )

  return (
    <div className="packaging">
      <div style={{ maxWidth: '50rem' }} className={'mb-4'}>
        <p className={'mt-3 decoration-gray-600'}>
          <Breadcrumbs routes={[{ path: ROUTES.generics, title: 'Generics Table' }]} />
        </p>
        <h2 className={'mb-5 mt-3 text-2xl'}>Generics</h2>
        <div className="packaging-actions items-center">
          <Input
            onChange={e => setDebouncedTableSearch(e.target.value)}
            iconType="search"
            placeholder="Search"
          />
          <Button onClick={() => navigate(`${ROUTES.generic}?itemId=new`)} variant="primary">
            Add Generic
          </Button>
          <ExportGraphqlButton payload={exportPayload} />
        </div>
      </div>
      <Table
        loading={loading}
        tableData={tableData}
        columnDefs={columnDefs}
        totalNumber={totalCount}
        endCursor={endCursor}
        startCursor={startCursor}
        refetch={refetch}
        defaultVars={genericsVars || getGenericsVars}
        setCursors={setCursors}
        tableSearch={tableSearch}
        setOrder={setOrder}
        order={order}
        suppressRowClickSelection
      />
    </div>
  )
}

export default Generics
